import { forwardRef, createElement } from "react"
import { useFormContext } from "./Form"
import { isInputableField } from "./helpers"
import { getControl } from "./types"
import ExpressionEngine from "web/services/expressions"
import { isNotAValue } from "web/services/helpers"

export const Field = forwardRef(
  (
    {
      field,
      autoFocus = false,
      autoSelect = false,
      onBlur,
      Control,
      value,
      error,
      touched,
      valueForConditions,
      onFieldChange,
      onFieldBlur,
      placeholder,
      submit,
      onFieldFocus,
      options,
      ...restOfProps
    },
    ref
  ) => {
    if (!isInputableField(field) || !field.isVisible) return null

    function _onBlur() {
      onBlur && onBlur()
    }

    function handleFieldChange(value) {
      onFieldChange(field.key, value)
    }

    function handleFieldBlur() {
      onFieldBlur(field.key)
      _onBlur()
    }

    const fieldProps = {
      ...field, // raw field definition,
      valueForConditions: valueForConditions,
      value,
      error,
      touched,
      placeholder,
      options,
      onChange: handleFieldChange,
      onFocus: () => onFieldFocus(field.key),
      onBlur: handleFieldBlur,
      onEnterKey: () => submit(),
      autoFocus,
      autoSelect,
      ...restOfProps,
    }

    const Comp = Control || getControl(field)
    return createElement(Comp, {
      ref: ref,
      key: field.id,
      ...fieldProps,
    })
  }
)

/**
 * This component takes the field name and looks up the
 * field definition from the form context.
 *
 * This makes it easier to use in custom layouts.
 */
const FieldWithFormContext = forwardRef(({ fieldKey, ...rest }, ref) => {
  const form = useFormContext()
  const formField = form.fields.find((f) => f.key === fieldKey)

  const options = formField.lookup_values?.filter(
    (option) =>
      isNotAValue(option.show_expression) ||
      ExpressionEngine.evaluate(
        option.show_expression,
        form.valueForConditions,
        formField.namespace
      )
  )

  const propsFromContext = {
    ...form,
    value: form.value[fieldKey],
    error: form.errors[fieldKey],
    touched: form.touched[fieldKey],
  }

  return (
    <Field
      ref={ref}
      field={formField}
      fieldKey={fieldKey}
      {...propsFromContext}
      options={options}
      {...rest}
    />
  )
})

export default FieldWithFormContext
